import { useTranslation } from "react-i18next";
import { jod } from "@astroplanet/shared";
import { money } from "../../lib/format";
import type { PaymentMethod } from "../../lib/types";

const ICON: Record<PaymentMethod, string> = { cash: "💵", card: "💳", cliq: "📲", credit: "🏛️" };

interface Props {
  value: PaymentMethod;
  onChange: (m: PaymentMethod) => void;
  /** Methods offered. Credit (ذمم) is only shown when the customer can use it. */
  methods?: PaymentMethod[];
}

export function PaymentPicker({ value, onChange, methods = ["cash", "card", "cliq"] }: Props) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-label={t("payment.method")} className="grid grid-flow-col auto-cols-fr gap-1.5">
      {methods.map((m) => (
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={value === m}
          onClick={() => onChange(m)}
          className={`rounded-xl border-2 px-2 py-2 text-xs font-black transition ${
            value === m ? "border-cosmic-purple bg-purple-50 text-cosmic-purple" : "border-gray-200 bg-white text-gray-700 hover:border-cosmic-purple"
          } ${m === "credit" ? "!border-amber-300 !bg-amber-50 !text-amber-900" : ""} ${m === "credit" && value === m ? "!border-amber-500" : ""}`}
        >
          <span aria-hidden>{ICON[m]}</span> {t(`payment.${m}`)}
        </button>
      ))}
    </div>
  );
}

interface TenderProps {
  total: number;
  tendered: string;
  onTendered: (v: string) => void;
}

/** Cash received → change due. `tendered` is a JOD string typed by the cashier. */
export function CashTender({ total, tendered, onTendered }: TenderProps) {
  const { t } = useTranslation();
  const given = tendered.trim() === "" ? null : jod(Number(tendered));
  const change = given === null || Number.isNaN(given) ? null : given - total;
  const quick = [total, Math.ceil(total / 5000) * 5000, Math.ceil(total / 10_000) * 10_000, 20_000].filter((v, i, a) => v >= total && a.indexOf(v) === i).slice(0, 4);
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
      <label className="label" htmlFor="tendered">{t("payment.cashReceived")}</label>
      <input id="tendered" className="field num font-mono" inputMode="decimal" value={tendered} onChange={(e) => onTendered(e.target.value)} placeholder={money(total)} />
      <div className="mt-2 flex flex-wrap gap-1.5">
        {quick.map((q) => (
          <button key={q} type="button" className="btn-ghost !px-2 !py-1" onClick={() => onTendered(money(q))}>
            <span className="num font-mono">{money(q)}</span>
          </button>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs font-black">
        <span>{t("payment.change")}</span>
        <span className={`num font-mono ${change !== null && change < 0 ? "text-red-600" : "text-emerald-700"}`}>
          {change === null ? "—" : change < 0 ? `${t("payment.short")} ${money(-change)}` : money(change)}
        </span>
      </div>
    </div>
  );
}
